import React from 'react';
import * as yup from 'yup';
import { FormikValues } from 'formik';
import { formatShortDate } from 'modules/Common/utilities';
import { FormikTextField, FormikCheckboxGroup } from 'modules/Common/components';
import { RecordDefinition } from 'modules/Pilot/BatchRecord/SingleView/components/RecordTable/RecordTable';
import { BatchRecord, LASER_NUM_MAX, LaserUsageSubRecord } from 'modules/Pilot/BatchRecord/domain';
import { DataTableRow } from 'modules/Pilot/BatchRecord/SingleView/components/SimpleDataTable/TableData';
import {
  recordLaserUsage,
  RecordLaserUsageActions,
  verifyLaserUsage,
  VerifyLaserUsageActions,
  amendLaserUsage,
  AmendLaserUsageActions,
} from 'modules/Pilot/BatchRecord/actions/laserUsage';
import { PartStatus } from 'modules/RecordManagement';
import { UserPermission } from 'modules/Core/types';
import getInitialSplits from '../../initialSplits';

interface Values extends FormikValues {
  splits: number[];
  laserFile: string;
  lasers: number[];
  comment?: string;
}

export default (
  batchRecord: BatchRecord,
  itemsBeingEdited: { key: string; itemId: string }[],
  userPermissions: UserPermission[]
): RecordDefinition<Values> => {
  const dialogName = 'pilot/batch-records/LASER';
  const recordId = batchRecord.id;
  const { splits } = batchRecord.batchRequest;
  const { status, subRecords, requiredPermissions } = batchRecord.preparation.laserUsage;
  const itemBeingEdited = itemsBeingEdited.find(
    item =>
      item.key === `${dialogName}_EDIT` &&
      subRecords &&
      subRecords.findIndex(print => print.id === item.itemId) > -1
  );

  const rowForEditing = itemBeingEdited
    ? subRecords.find(print => print.id === itemBeingEdited.itemId)
    : null;

  const laserOptions: number[] = [];
  for (let i = 1; i < LASER_NUM_MAX + 1; i += 1) laserOptions.push(i);

  const isAllowedToEdit = () => {
    if (status === PartStatus.ReadOnly || status === PartStatus.Locked) return false;

    let result = true;
    requiredPermissions?.forEach(req => {
      if (userPermissions.find(perm => perm.name === req) === undefined) result = false;
    });

    return result;
  };

  const newSubmit = (values: Values, _, dispatch) =>
    dispatch(recordLaserUsage(recordId, { ...values }));
  const editSubmit = (values: Values, _, dispatch) =>
    dispatch(amendLaserUsage(recordId, { ...values, subRecordId: rowForEditing?.id }));

  const validation = {
    splits: yup
      .array()
      .of(yup.number().defined())
      .defined()
      .min(1)
      .max(splits.length),
    laserFile: yup
      .string()
      .defined()
      .min(1),
    lasers: yup
      .array()
      .of(yup.number().defined())
      .defined()
      .min(1)
      .max(LASER_NUM_MAX),
  };

  const editValidation = {
    ...validation,
    comment: yup
      .string()
      .defined()
      .required()
      .min(5),
  };

  const createDataTableRow = (row: LaserUsageSubRecord, index: number): DataTableRow => ({
    key: row.id,
    fields: {
      splits: { value: row.splits.join(', ') },
      laserFile: { value: row.laserFile },
      lasers: { value: row.lasers.join(', ') },
      runBy: { user: row.runBy.user.name, date: formatShortDate(row.runBy.date) },
      verifiedBy: {
        user: row.verification ? row.verification.user.name : null,
        date: formatShortDate(row.verification?.date),
      },
    },
    verification: {
      title: 'Verify Laser Run',
      subRecordId: row.id,
      dialogName: `pilot/batch-records/SIGN_OFF_LASER_${index}`,
      action: verifyLaserUsage(recordId, row.id),
      api: VerifyLaserUsageActions.CALL,
      maxWidth: 'md',
      review: [
        { title: 'Split(s)', value: row.splits.join(', ') },
        { title: 'Laser Number(s)', value: row.lasers.join(', ') },
        { title: 'Laser File', value: row.laserFile },
        { title: 'Run By', value: row.runBy.user.name },
        { title: 'Run Date', value: formatShortDate(row.runBy.date) },
      ],
    },
  });

  return {
    title: 'Laser',
    tableData: {
      columns: [
        { accessor: 'splits', label: 'Split(s)' },
        { accessor: 'laserFile', label: 'Laser File' },
        { accessor: 'lasers', label: 'Laser Number(s)' },
        { accessor: 'runBy', label: 'Run by', isSignOff: true },
        { accessor: 'verifiedBy', label: 'Verified by', isSignOff: true, isVerification: true },
      ],
      rows: subRecords ? subRecords.map((row, index) => createDataTableRow(row, index)) : [],
    },
    canAddRow: status === PartStatus.Incomplete,
    canEditRows: isAllowedToEdit(),
    formDefinition: {
      title: 'Laser',
      dialogName,
      apiAction: rowForEditing ? AmendLaserUsageActions.CALL : RecordLaserUsageActions.CALL,
      onSubmit: rowForEditing ? editSubmit : newSubmit,
      initialValues: {
        splits: getInitialSplits(rowForEditing, splits),
        laserFile: rowForEditing ? rowForEditing.laserFile : '',
        lasers: rowForEditing ? rowForEditing.lasers : [],
      },
      form: () => (
        <>
          <FormikCheckboxGroup
            name="splits"
            label="Splits"
            options={splits}
            labelPlacement="top"
            allowSelectAll
          />
          <br />
          <FormikCheckboxGroup
            name="lasers"
            label="Laser Numbers"
            options={laserOptions}
            labelPlacement="top"
          />
          <FormikTextField name="laserFile" label="Laser File" />
          {rowForEditing && <FormikTextField name="comment" label="Reason for change" multiline />}
        </>
      ),
      validationSchema: (): yup.ObjectSchema<Values> =>
        yup
          .object()
          .shape(rowForEditing ? editValidation : validation)
          .defined(),
    },
  };
};
